import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  Autocomplete,
  CircularProgress,
  Backdrop,
  InputAdornment,
  Snackbar,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Payment as PaymentIcon,
  CheckCircle as SyncedIcon,
  HourglassEmpty as PendingIcon,
  Error as FailedIcon,
  Delete as DeleteIcon,
  AccountBalanceWallet as TopupIcon,
  Info as InfoIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  Receipt as InvoiceIcon,
  CloudUpload as UploadIcon,
  AccountBalance as BankIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import ApiService from '../services/api.service';
import SyncToThaklisDialog from '../components/SyncToThaklisDialog';
import { isPlatformLevel, can } from '../utils/roleUtils';
import { getBrandContrastText, getBrandGradient, getBrandedTableHeadSx } from '../utils/colorUtils';

const Payments = ({ currentUser }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  // Role check helper
  const isPlatform = isPlatformLevel(currentUser?.role);

  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({
    attemptedAmount: 0,
    paidToThaklis: 0,
    count: 0,
    paid: 0,
    pending: 0,
    rejected: 0
  });
  const [ticketSummary, setTicketSummary] = useState({
    totalTickets: 0,
    totalCost: 0,
    completed: { paidAmount: 0, paidCount: 0, unpaidAmount: 0, unpaidCount: 0 },
    uncompleted: { paidAmount: 0, paidCount: 0, unpaidAmount: 0, unpaidCount: 0 },
    totalPaidAmount: 0, totalPaidCount: 0,
    totalUnpaidAmount: 0, totalUnpaidCount: 0
  });
  // Snapshot summary (overall, NOT date-filtered) for the Paid to Thaklis +
  // Payments via Pro tiles. Source: GET /api/payments/via-pro-summary.
  const [viaProSummary, setViaProSummary] = useState({
    paidAmount: 0, paidCount: 0,
    inReviewAmount: 0, inReviewCount: 0,
    unpaidAmount: 0, unpaidCount: 0
  });
  const [loading, setLoading] = useState(false);
  const [openRecordDialog, setOpenRecordDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openTopupDialog, setOpenTopupDialog] = useState(false);
  const [openDirectTopupDialog, setOpenDirectTopupDialog] = useState(false);
  const [openSyncDialog, setOpenSyncDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupDestinationOrgId, setTopupDestinationOrgId] = useState(null);
  const [directTopupData, setDirectTopupData] = useState({
    organizationId: null,
    amount: '',
    reason: '',
    notes: ''
  });
  const [selectedTypingCenter, setSelectedTypingCenter] = useState(
    isPlatform ? '' : currentUser?.typingCenterId
  );
  const [typingCenters, setTypingCenters] = useState([]);
  const [mappedOrganizations, setMappedOrganizations] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountsError, setBankAccountsError] = useState(null);
  // TC's own bank accounts (source for auto-debit on offline Thaklis payments)
  const [sourceBankAccounts, setSourceBankAccounts] = useState([]);
  const [sourceBankInfo, setSourceBankInfo] = useState(null); // { balance, available, pendingDebits }
  const [sourceBankInfoLoading, setSourceBankInfoLoading] = useState(false);
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoicesFetchError, setInvoicesFetchError] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    organizationId: null,
    invoiceId: null,
    amount: '',
    // Payment routing — mirrors Thaklis Web's PaymentTypeStep values.
    // 'OFFLINE' = bank/cash + proof; 'GATEWAY' = hosted card payment.
    paymentType: 'OFFLINE',
    paymentGateway: 'NGENIUS',
    paymentMethod: 'BANK_TRANSFER',
    paymentDate: new Date().toISOString().split('T')[0],
    referenceNumber: '',
    bankDepositDate: new Date().toISOString().split('T')[0],
    notes: '',
    // New: Destination bank account
    destinationBankAccountId: null,
    // New: TC source bank account to auto-debit on confirmation (offline)
    sourceBankAccountId: null,
    // New: Receipt upload
    receiptFile: null,
    receiptUrl: null,
    receiptS3Key: null,
    receiptFilename: null
  });
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [initiatingOnline, setInitiatingOnline] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // NGenius online platform-fee config (from Thaklis Main, the single source that
  // actually surcharges the gateway order). Used to preview the surcharge.
  const [platformFeeConfig, setPlatformFeeConfig] = useState({ fee_percentage: 0, fee_fixed: 0 });

  // Platform-fee surcharge for an online amount. Mirrors the backend rounding exactly:
  // round the percentage part to 2dp first, then add the fixed fee (HALF_UP).
  const computePlatformFee = (amount) => {
    const amt = parseFloat(amount) || 0;
    if (amt <= 0) return 0;
    const pctPart = Math.round((amt * (platformFeeConfig.fee_percentage || 0)) / 100 * 100) / 100;
    return Math.round((pctPart + (platformFeeConfig.fee_fixed || 0)) * 100) / 100;
  };

  // Catalog of online payment gateways. Only NGenius is currently wired up;
  // the others are placeholders so the dropdown matches the Thaklis Web UI.
  const PAYMENT_GATEWAYS = [
    { id: 'NGENIUS', label: 'NGenius (Card)', enabled: true, note: 'Credit / Debit card via NGenius' },
    { id: 'STRIPE',  label: 'Stripe',         enabled: false, note: 'Coming soon' },
    { id: 'TABBY',   label: 'Tabby',          enabled: false, note: 'Coming soon' },
    { id: 'NOQUDI',  label: 'NoQudi',         enabled: false, note: 'Coming soon' }
  ];

  // Date range filter state
  const [dateRange, setDateRange] = useState('30_DAYS');
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Calculate date range for API
  const getDateRange = () => {
    const now = new Date();
    let startDate = null;
    let endDate = now.toISOString();

    switch (dateRange) {
      case '24_HR':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
        break;
      case '7_DAYS':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case '30_DAYS':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case '90_DAYS':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case 'CUSTOM':
        startDate = customStartDate ? new Date(customStartDate).toISOString() : null;
        endDate = customEndDate ? new Date(customEndDate + 'T23:59:59').toISOString() : now.toISOString();
        break;
      default:
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    }

    return { startDate, endDate };
  };

  // Load the NGenius online platform-fee config once (from Thaklis Main) so the online
  // payment summary can preview the surcharge.
  useEffect(() => {
    let active = true;
    ApiService.getOnlinePlatformFee()
      .then((res) => {
        if (!active) return;
        const data = res?.data?.data || res?.data || {};
        setPlatformFeeConfig({
          fee_percentage: Number(data.fee_percentage) || 0,
          fee_fixed: Number(data.fee_fixed) || 0,
        });
      })
      .catch(() => { /* no fee config → no surcharge shown */ });
    return () => { active = false; };
  }, []);

  // Platform users pick a typing center; default to the first real one
  useEffect(() => {
    if (!isPlatform) return;
    ApiService.getTypingCenters()
      .then((res) => {
        const list = res.data?.data || [];
        setTypingCenters(list);
        setSelectedTypingCenter(prev => prev || list[0]?.id || '');
      })
      .catch((err) => console.error('Failed to fetch typing centers:', err));
  }, [isPlatform]);

  // Fetch available invoices when organization is selected (exclude already paid ones)
  useEffect(() => {
    const fetchInvoicesForOrg = async () => {
      if (!formData.organizationId || !selectedTypingCenter) {
        setAvailableInvoices([]);
        return;
      }

      setLoadingInvoices(true);
      setInvoicesFetchError(null);
      try {
        console.log(`Fetching invoices for org: ${formData.organizationId}`);

        // Backend filter handles all eligibility:
        // - status = FINAL
        // - internal_total > 0 (has ticket amount)
        // - thaklis_payment_status NOT IN (PENDING, PAID)
        // The endpoint also fails (503/409) if any ticket's status can't be
        // verified or is in WAITING_FOR_APPROVAL — surface that to the user
        // instead of silently rendering an empty picker.
        const response = await ApiService.getInvoicesPayableToThaklis(selectedTypingCenter);
        const all = response?.data?.data || [];
        // Local filter: scope to selected organization
        const filtered = formData.organizationId
          ? all.filter(inv => (inv.organization_id || inv.organizationId) === formData.organizationId)
          : all;
        setAvailableInvoices(filtered);
      } catch (error) {
        console.error('Error fetching invoices:', error);
        const msg = error?.response?.data?.error
          || error?.response?.data?.message
          || error?.message
          || 'Could not load invoices. Please retry shortly.';
        setInvoicesFetchError(msg);
        setAvailableInvoices([]);
      } finally {
        setLoadingInvoices(false);
      }
    };

    fetchInvoicesForOrg();
  }, [formData.organizationId, selectedTypingCenter]);

  // Fetch mapped organizations for the typing center
  useEffect(() => {
    const fetchMappedOrganizations = async () => {
      if (!selectedTypingCenter) return;

      try {
        console.log(`Fetching mapped organizations for typing center: ${selectedTypingCenter}`);
        const response = await ApiService.get(`/api/typing-centers/${selectedTypingCenter}/with-mappings`);

        if (response.data && response.data.success) {
          const mappings = response.data.data.mapped_organizations || [];
          // Extract unique organizations from mappings
          const orgs = mappings.map(m => ({
            id: m.organizationId,
            name: m.organizationName,
            code: m.organizationCode
          }));
          setMappedOrganizations(orgs);
          console.log(`Found ${orgs.length} mapped organizations`);
        }
      } catch (error) {
        console.error('Error fetching mapped organizations:', error);
        setMappedOrganizations([]);
      }
    };

    fetchMappedOrganizations();
  }, [selectedTypingCenter]);

  // Fetch ticket payment summary from Thaklis Platform (via Managers backend)
  useEffect(() => {
    const fetchTicketSummary = async () => {
      const centerId = isPlatform ? selectedTypingCenter : currentUser?.typingCenterId;
      if (!centerId) return;
      try {
        const response = await ApiService.get(`/api/finance/ticket-payment-summary?typingCenterId=${centerId}`);
        if (response.data) {
          setTicketSummary(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch ticket payment summary:', error);
      }
    };
    fetchTicketSummary();
  }, [selectedTypingCenter, currentUser, isPlatform]);

  // Fetch via-Pro snapshot — overall counters that drive tiles 2 & 4 and
  // are independent of the date-range filter at the top of the page.
  useEffect(() => {
    const fetchViaProSummary = async () => {
      const centerId = isPlatform ? selectedTypingCenter : currentUser?.typingCenterId;
      if (!centerId) return;
      try {
        const response = await ApiService.getPaymentsViaProSummary(centerId);
        const data = response?.data?.data || response?.data;
        if (data) setViaProSummary(data);
      } catch (error) {
        console.error('Failed to fetch via-Pro summary:', error);
      }
    };
    fetchViaProSummary();
  }, [selectedTypingCenter, currentUser, isPlatform, payments]);

  // Load payments based on user role and selected typing center
  useEffect(() => {
    const fetchPayments = async () => {
      setLoading(true);
      try {
        const centerId = isPlatform ? selectedTypingCenter : currentUser?.typingCenterId;
        const { startDate, endDate } = getDateRange();

        // Fetch payments from API filtered by typing center + date range
        const response = await ApiService.getOrganizationPayments({
          typingCenterId: centerId,
          includeDeleted: false,
          startDate,
          endDate
        });

        setPayments(response.data.data || []);
        if (response.data.summary) {
          setSummary({
            ...response.data.summary,
            count: response.data.count ?? (response.data.data || []).length
          });
        }
      } catch (error) {
        console.error('Failed to load payments:', error);
        // Fallback to empty array on error
        setPayments([]);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchPayments();
    }
  }, [currentUser, selectedTypingCenter, dateRange, customStartDate, customEndDate]);

  // Fetch Thaklis bank accounts (destination for TC→Thaklis topups)
  useEffect(() => {
    const fetchBankAccounts = async () => {
      if (!selectedTypingCenter) return;

      try {
        const bankAccountsResponse = await ApiService.getAvailableBankAccounts();
        if (bankAccountsResponse.data && bankAccountsResponse.data.success) {
          setBankAccounts(bankAccountsResponse.data.data || []);
          setBankAccountsError(null);
        } else {
          setBankAccounts([]);
          setBankAccountsError('Failed to fetch Thaklis bank accounts');
        }
      } catch (error) {
        console.error('Error fetching Thaklis bank accounts:', error);
        setBankAccounts([]);
        setBankAccountsError('Failed to fetch Thaklis bank accounts');
      }
    };

    fetchBankAccounts();
  }, [selectedTypingCenter]);

  // Fetch the TC's OWN bank accounts (source to auto-debit on offline payments)
  useEffect(() => {
    const fetchSourceBankAccounts = async () => {
      if (!selectedTypingCenter) return;
      try {
        const res = await ApiService.getTcBankAccounts(isPlatform ? selectedTypingCenter : undefined);
        const accounts = (res?.data?.data || []).filter(a => a.is_active);
        setSourceBankAccounts(accounts);
      } catch (error) {
        console.error('Error fetching TC source bank accounts:', error);
        setSourceBankAccounts([]);
      }
    };
    fetchSourceBankAccounts();
  }, [selectedTypingCenter, isPlatform]);

  // Refresh available-balance snapshot when the selected source bank changes
  useEffect(() => {
    const fetchAvailable = async () => {
      const bankId = formData.sourceBankAccountId;
      if (!bankId || !selectedTypingCenter) {
        setSourceBankInfo(null);
        return;
      }
      setSourceBankInfoLoading(true);
      try {
        const res = await ApiService.getSourceBankAvailable(selectedTypingCenter, bankId);
        setSourceBankInfo(res?.data?.data || null);
      } catch (error) {
        console.error('Error fetching source bank available balance:', error);
        setSourceBankInfo(null);
      } finally {
        setSourceBankInfoLoading(false);
      }
    };
    fetchAvailable();
  }, [formData.sourceBankAccountId, selectedTypingCenter]);

  // Get organizations mapped to current typing center
  const getAvailableOrganizations = () => {
    return mappedOrganizations;
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setSubmitting(true);
      setSubmitError(null);
      const centerId = isPlatform ? selectedTypingCenter : currentUser?.typingCenterId;
      const org = mappedOrganizations.find(o => o.id === formData.organizationId);
      const selectedInvoice = availableInvoices.find(inv => inv.id === formData.invoiceId);

      // Online (hosted payment) branch — no bank account, no proof upload.
      // Backend creates the TPayment + Thaklis topup and returns a hosted
      // payment page URL; we redirect the browser to it.
      if (formData.paymentType === 'GATEWAY') {
        setInitiatingOnline(true);
        const onlineData = {
          typingCenterId: centerId,
          organizationId: formData.organizationId,
          organizationName: org?.name || '',
          organizationCode: org?.code || '',
          paymentDate: new Date(formData.paymentDate).toISOString(),
          notes: formData.notes,
          invoiceId: formData.invoiceId || null,
          invoiceNumber: selectedInvoice?.invoice_number || null,
          paymentGateway: formData.paymentGateway
        };
        const response = await ApiService.initiateOnlinePayment(onlineData);
        const paymentUrl = response?.data?.data?.paymentUrl;
        if (!paymentUrl) {
          alert('Online payment could not be started: no payment URL returned.');
          return;
        }
        // Full-page redirect — Thaklis Web does the same.
        window.location.href = paymentUrl;
        return;
      }

      const selectedBank = bankAccounts.find(b => b.id === formData.destinationBankAccountId);

      const paymentData = {
        typingCenterId: centerId,
        organizationId: formData.organizationId,
        organizationName: org?.name || '',
        organizationCode: org?.code || '',
        amount: parseFloat(formData.amount),
        paymentMethod: formData.paymentMethod,
        paymentDate: new Date(formData.paymentDate).toISOString(),
        paymentReference: formData.referenceNumber || `REF-${Date.now()}`,
        bankDepositReference: formData.bankDepositDate,
        notes: formData.notes,
        invoiceId: formData.invoiceId || null,
        invoiceNumber: selectedInvoice?.invoice_number || null,
        destinationBankAccountId: formData.destinationBankAccountId || null,
        destinationBankAccountName: selectedBank?.accountName || null,
        destinationBankName: selectedBank?.bankName || null,
        // TC source bank to auto-debit on confirmation (offline)
        sourceBankAccountId: formData.sourceBankAccountId || null
        // Receipt is uploaded separately AFTER payment creation
      };

      const response = await ApiService.createOrganizationPayment(paymentData);

      if (response.data.success) {
        const created = response.data.data;
        const paymentId = created.id;
        const topupId = created.thaklis_topup_request_id || created.thaklisTopupRequestId;

        // Upload proof to BOTH Manager S3 and Thaklis S3 in parallel.
        // The two uploads are independent — failure of one does not affect the other,
        // and neither rolls back the payment.
        if (formData.receiptFile && paymentId) {
          const tasks = [
            ApiService.uploadPaymentProofToManager(paymentId, formData.receiptFile)
              .catch(err => { console.error('Manager proof upload failed:', err); return { failed: 'manager' }; })
          ];
          if (topupId) {
            tasks.push(
              ApiService.uploadPaymentProofToThaklis(topupId, formData.receiptFile)
                .catch(err => { console.error('Thaklis proof upload failed:', err); return { failed: 'thaklis' }; })
            );
          }
          const results = await Promise.all(tasks);
          const failures = results.filter(r => r && r.failed).map(r => r.failed);
          if (failures.length > 0) {
            alert(`Payment submitted, but proof upload failed for: ${failures.join(', ')}. You can retry later. The status will be reflected within 24 hours.`);
          } else {
            alert('Payment submitted. The status will be reflected within 24 hours.');
          }
        } else {
          alert('Payment submitted. The status will be reflected within 24 hours.');
        }

        // Refresh the latest payment row (with updated receipt fields)
        try {
          const refreshed = await ApiService.getOrganizationPayments({
            typingCenterId: selectedTypingCenter,
            includeDeleted: false
          });
          if (refreshed.data?.data) setPayments(refreshed.data.data);
        } catch {
          setPayments([created, ...payments]);
        }
        setOpenRecordDialog(false);
        resetForm();
      }
    } catch (error) {
      console.error('Failed to create payment:', error);
      const msg = error?.response?.data?.error
        || error?.response?.data?.message
        || error?.message
        || 'Failed to create payment. Please try again.';
      setSubmitError(msg);
    } finally {
      setLoading(false);
      setInitiatingOnline(false);
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      organizationId: null,
      invoiceId: null,
      amount: '',
      paymentType: 'OFFLINE',
      paymentGateway: 'NGENIUS',
      paymentMethod: 'BANK_TRANSFER',
      paymentDate: new Date().toISOString().split('T')[0],
      referenceNumber: '',
      bankDepositDate: new Date().toISOString().split('T')[0],
      notes: '',
      destinationBankAccountId: null,
      sourceBankAccountId: null,
      receiptFile: null,
      receiptUrl: null,
      receiptS3Key: null,
      receiptFilename: null
    });
    setAvailableInvoices([]);
    setInvoicesFetchError(null);
    setSubmitError(null);
    setSourceBankInfo(null);
  };

  // Handle receipt file selection — file is uploaded to Manager + Thaklis
  // AFTER the payment is created (in handleSubmit). Here we just validate
  // and stash the file locally.
  const handleReceiptUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type (matches Thaklis: image/* and application/pdf)
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      alert('Invalid file type. Supported: image/* and PDF.');
      return;
    }

    // Validate file size (3MB)
    if (file.size > 3 * 1024 * 1024) {
      alert('File too large. Maximum size is 3MB.');
      return;
    }

    setFormData(prev => ({ ...prev, receiptFile: file, receiptFilename: file.name }));
  };

  // Handle topup wallet - creates topup request in Thaklis
  const handleTopupWallet = async () => {
    try {
      setLoading(true);
      const amountToTopup = parseFloat(topupAmount);

      // Create topup request in Thaklis
      const topupData = {
        amount: amountToTopup,
        organizationId: topupDestinationOrgId,
        comment: `Payment topup from ${selectedPayment.organization_name} - ${selectedPayment.payment_reference || selectedPayment.bank_deposit_reference}`
      };

      console.log('Creating topup request:', topupData);

      const response = await ApiService.topupWalletFromPayment(selectedPayment.id, topupData);

      if (response.data.success) {
        // Update payment in list - the response now contains both payment and thaklisTopupRequest
        const updatedPayment = response.data.data.payment || response.data.data;
        const updatedPayments = payments.map(p =>
          p.id === selectedPayment.id ? updatedPayment : p
        );
        setPayments(updatedPayments);
        setOpenTopupDialog(false);
        setSelectedPayment(null);
        setTopupAmount('');
        setTopupDestinationOrgId(null);

        // Show success message with topup request details
        alert(`Topup request created successfully! A topup request of ${amountToTopup} AED has been submitted to Thaklis for approval.`);
      }
    } catch (error) {
      console.error('Failed to topup wallet:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to topup wallet. Please try again.';
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle direct topup - topup without payment record
  const handleDirectTopup = async () => {
    try {
      setLoading(true);
      const centerId = isPlatform ? selectedTypingCenter : currentUser?.typingCenterId;
      const org = getAvailableOrganizations().find(o => o.id === directTopupData.organizationId);

      // Create a payment record with DIRECT_TOPUP method
      const paymentData = {
        typingCenterId: centerId,
        organizationId: directTopupData.organizationId,
        organizationName: org?.name || '',
        organizationCode: org?.code || '',
        amount: parseFloat(directTopupData.amount),
        paymentMethod: 'DIRECT_TOPUP',
        paymentDate: new Date().toISOString(),
        paymentReference: `DIRECT-${Date.now()}`,
        bankDepositReference: `DIRECT-${new Date().toISOString().split('T')[0].replace(/-/g, '')}`,
        notes: `Direct Topup - Reason: ${directTopupData.reason}${directTopupData.notes ? ` | Notes: ${directTopupData.notes}` : ''}`,
        metadata: {
          reason: directTopupData.reason,
          notes: directTopupData.notes
        }
      };

      const response = await ApiService.createOrganizationPayment(paymentData);

      if (response.data.success) {
        // Add new direct topup payment to the list
        setPayments([response.data.data, ...payments]);
        setOpenDirectTopupDialog(false);
        setDirectTopupData({
          organizationId: null,
          amount: '',
          reason: '',
          notes: ''
        });
      }
    } catch (error) {
      console.error('Failed to create direct topup:', error);
      alert('Failed to create direct topup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle refresh approval status from Thaklis
  const handleRefreshApprovalStatus = async (payment) => {
    try {
      setLoading(true);
      const response = await ApiService.post(`/api/payments/${payment.id}/refresh-approval-status`);

      if (response.data.success) {
        // Re-fetch full payments list to update both the row and the stats
        const centerId = isPlatform ? selectedTypingCenter : currentUser?.typingCenterId;
        if (centerId) {
          const refreshed = await ApiService.getOrganizationPayments({
            typingCenterId: centerId,
            includeDeleted: false,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
          });
          if (refreshed.data) {
            setPayments(refreshed.data.data || []);
            setSummary(refreshed.data.summary || {});
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh approval status:', error);
      alert('Failed to refresh approval status');
    } finally {
      setLoading(false);
    }
  };

  // Handle sync to Thaklis completion
  const handleSyncComplete = async (result) => {
    console.log('Sync to Thaklis completed:', result);

    // Refresh payments list to get updated status
    try {
      const centerId = isPlatform ? selectedTypingCenter : currentUser?.typingCenterId;
      const response = await ApiService.getOrganizationPayments({
        typingCenterId: centerId,
        includeDeleted: false
      });
      setPayments(response.data.data || []);
      if (response.data.summary) {
        setSummary({
          ...response.data.summary,
          count: response.data.count ?? (response.data.data || []).length
        });
      }
    } catch (error) {
      console.error('Failed to refresh payments:', error);
    }

    setOpenSyncDialog(false);
    setSelectedPayment(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Offline auto-debit guard: does the entered amount exceed the source bank's
  // available (balance − pending reservations)?
  const amountExceedsAvailable =
    formData.paymentType === 'OFFLINE' &&
    !!formData.sourceBankAccountId &&
    sourceBankInfo != null &&
    formData.amount !== '' && formData.amount != null &&
    parseFloat(formData.amount) > Number(sourceBankInfo.available);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Statistics by paymentStatus (TC → Thaklis): paid / pending / rejected
  const stats = {
    total: summary.count ?? payments.length,
    attemptedAmount: summary.attemptedAmount || 0,
    paidToThaklis: summary.paidToThaklis || 0,
    paid: summary.paid || 0,
    pending: summary.pending || 0,
    rejected: summary.rejected || 0
  };

  return (
    <Box>
      {/* Submission errors float above the Record Payment dialog so the user
          sees them regardless of dialog scroll position. zIndex sits above
          the modal layer. Auto-hides after 8s; click X to dismiss. */}
      <Snackbar
        open={!!submitError}
        autoHideDuration={8000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setSubmitError(null)}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setSubmitError(null)}
          sx={{ width: '100%', maxWidth: 600 }}
        >
          {submitError}
        </Alert>
      </Snackbar>

      {/* Full-page loading overlay during payment submission — blocks the user
          from double-submitting while the Thaklis topup API call is in flight. */}
      <Backdrop
        open={submitting}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, flexDirection: 'column', gap: 2 }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {initiatingOnline
            ? 'Preparing your secure payment page…'
            : 'Submitting payment…'}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.85 }}>
          Please do not close or refresh this page.
        </Typography>
      </Backdrop>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.main' }}>
            Thaklis Payments
          </Typography>
          {isPlatform && (
            <FormControl size="small" sx={{ minWidth: 250 }}>
              <Select
                value={selectedTypingCenter}
                onChange={(e) => setSelectedTypingCenter(e.target.value)}
                displayEmpty
                sx={{ bgcolor: 'background.paper', fontSize: '0.875rem' }}
              >
                {typingCenters.map(center => (
                  <MenuItem key={center.id} value={center.id} sx={{ fontSize: '0.875rem' }}>
                    {center.name} ({center.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Direct Topup hidden for now — kept here for future re-enable.
          <Button
            variant="contained"
            startIcon={<TopupIcon />}
            onClick={() => setOpenDirectTopupDialog(true)}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
            }}
          >
            Direct Topup
          </Button>
          */}
          {can(currentUser, 'thaklis_features', 'write') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenRecordDialog(true)}
              sx={{
                textTransform: 'none',
                fontSize: '0.875rem',
              }}
            >
              Record Payment
            </Button>
          )}
        </Box>
      </Box>

      {/* Ticket Payment Overview — Dashboard-style teal cards, 2 rows */}
      {(() => {
        const overviewScope = isPlatform ? 'platform' : 'tenant';
        const overviewText = isDark ? 'text.primary' : getBrandContrastText(theme, overviewScope);
        const tealCard = {
          background: isDark ? 'background.paper' : getBrandGradient(theme, overviewScope),
          color: overviewText,
          borderRadius: 2,
          height: '100%',
          border: isDark ? '1px solid' : 'none',
          borderColor: isDark ? 'divider' : 'transparent'
        };
        const tealContent = { py: 1.5, px: 2, '&:last-child': { pb: 1.5 }, height: '100%' };
        const titleSx = { color: isDark ? 'text.secondary' : overviewText, opacity: isDark ? 1 : 0.72, fontSize: '0.75rem', fontWeight: 500 };
        const rowSx = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 };
        const countSx = { color: isDark ? 'text.secondary' : overviewText, opacity: isDark ? 1 : 0.65, fontSize: '0.85rem', fontWeight: 600 };
        const labelSx = { fontSize: '0.7rem', color: isDark ? 'text.disabled' : overviewText, opacity: isDark ? 1 : 0.58 };
        const divider = { borderTop: '1px solid', borderColor: isDark ? 'divider' : 'rgba(255,255,255,0.15)' };
        return (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* Row 1: Total Cost | Paid to Thaklis | Unpaid | Paid via Pro */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={tealCard}>
                  <CardContent sx={tealContent}>
                    <Typography sx={titleSx}>Total Ticket Cost</Typography>
                    <Box sx={rowSx}>
                      <Typography sx={{ color: '#73BF69', fontWeight: 700, fontSize: '1.1rem' }}>
                        {formatCurrency((ticketSummary.completed?.paidAmount || 0) + (ticketSummary.completed?.unpaidAmount || 0))} <Typography component="span" sx={{ ...labelSx }}>(Completed)</Typography>
                      </Typography>
                      <Typography sx={countSx}>{(ticketSummary.completed?.paidCount || 0) + (ticketSummary.completed?.unpaidCount || 0)}</Typography>
                    </Box>
                    <Box sx={{ ...rowSx, ...divider }}>
                      <Typography sx={{ color: '#FF9830', fontWeight: 700, fontSize: '1.1rem' }}>
                        {formatCurrency((ticketSummary.uncompleted?.paidAmount || 0) + (ticketSummary.uncompleted?.unpaidAmount || 0))} <Typography component="span" sx={{ ...labelSx }}>(In Progress)</Typography>
                      </Typography>
                      <Typography sx={countSx}>{(ticketSummary.uncompleted?.paidCount || 0) + (ticketSummary.uncompleted?.unpaidCount || 0)}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={tealCard}>
                  <CardContent sx={tealContent}>
                    <Typography sx={titleSx}>Paid to Thaklis</Typography>
                    <Box sx={rowSx}>
                      <Typography sx={{ color: '#73BF69', fontWeight: 700, fontSize: '1.1rem' }}>
                        {formatCurrency(Math.max(0, (ticketSummary.totalPaidAmount || 0) - (viaProSummary.paidAmount || 0)))} <Typography component="span" sx={{ ...labelSx }}>(Direct)</Typography>
                      </Typography>
                      <Typography sx={countSx}>{Math.max(0, (ticketSummary.totalPaidCount || 0) - (viaProSummary.paidCount || 0))}</Typography>
                    </Box>
                    <Box sx={{ ...rowSx, ...divider }}>
                      <Typography sx={{ color: '#5794F2', fontWeight: 700, fontSize: '1.1rem' }}>
                        {formatCurrency(viaProSummary.paidAmount || 0)} <Typography component="span" sx={{ ...labelSx }}>(Via Pro)</Typography>
                      </Typography>
                      <Typography sx={countSx}>{viaProSummary.paidCount || 0}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={tealCard}>
                  <CardContent sx={tealContent}>
                    <Typography sx={titleSx}>Unpaid</Typography>
                    {ticketSummary.totalUnpaidCount > 0 ? (
                      <>
                        <Box sx={rowSx}>
                          <Typography sx={{ color: '#FF9830', fontWeight: 700, fontSize: '1.1rem' }}>
                            {formatCurrency(ticketSummary.completed?.unpaidAmount || 0)} <Typography component="span" sx={{ ...labelSx }}>(Completed)</Typography>
                          </Typography>
                          <Typography sx={countSx}>{ticketSummary.completed?.unpaidCount || 0}</Typography>
                        </Box>
                        <Box sx={{ ...rowSx, ...divider }}>
                          <Typography sx={{ color: '#E02F44', fontWeight: 700, fontSize: '1.1rem' }}>
                            {formatCurrency(ticketSummary.uncompleted?.unpaidAmount || 0)} <Typography component="span" sx={{ ...labelSx }}>(In Progress)</Typography>
                          </Typography>
                          <Typography sx={countSx}>{ticketSummary.uncompleted?.unpaidCount || 0}</Typography>
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60px' }}>
                        <Typography sx={{ color: '#73BF69', fontWeight: 700, fontSize: '1.2rem' }}>All Paid</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={tealCard}>
                  <CardContent sx={tealContent}>
                    <Typography sx={titleSx}>Payments via Pro</Typography>
                    <Box sx={rowSx}>
                      <Typography sx={{ color: '#73BF69', fontWeight: 700, fontSize: '1.1rem' }}>
                        {formatCurrency(viaProSummary.paidAmount || 0)} <Typography component="span" sx={{ ...labelSx }}>(Paid)</Typography>
                      </Typography>
                      <Typography sx={countSx}>{viaProSummary.paidCount || 0}</Typography>
                    </Box>
                    <Box sx={{ ...rowSx, ...divider }}>
                      <Typography sx={{ color: '#FF9830', fontWeight: 700, fontSize: '1.1rem' }}>
                        {formatCurrency(viaProSummary.inReviewAmount || 0)} <Typography component="span" sx={{ ...labelSx }}>(In Review)</Typography>
                      </Typography>
                      <Typography sx={countSx}>{viaProSummary.inReviewCount || 0}</Typography>
                    </Box>
                    <Box sx={{ ...rowSx, ...divider }}>
                      <Typography sx={{ color: '#E02F44', fontWeight: 700, fontSize: '1.1rem' }}>
                        {formatCurrency(viaProSummary.unpaidAmount || 0)} <Typography component="span" sx={{ ...labelSx }}>(Unpaid)</Typography>
                      </Typography>
                      <Typography sx={countSx}>{viaProSummary.unpaidCount || 0}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        );
      })()}

      {/* Date Range Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>
              Date Range:
            </Typography>
            <ButtonGroup size="small" variant="outlined">
              {[
                { value: '24_HR', label: '24 HR' },
                { value: '7_DAYS', label: '7 DAYS' },
                { value: '30_DAYS', label: '30 DAYS' },
                { value: '90_DAYS', label: '90 DAYS' },
                { value: 'CUSTOM', label: 'CUSTOM' }
              ].map((option) => (
                <Button
                  key={option.value}
                  onClick={() => {
                    if (option.value === 'CUSTOM') {
                      setCustomDateOpen(true);
                    } else {
                      setDateRange(option.value);
                    }
                  }}
                  sx={{
                    fontSize: '0.75rem',
                    px: 1.5,
                    bgcolor: dateRange === option.value ? 'secondary.main' : 'transparent',
                    color: dateRange === option.value ? 'secondary.contrastText' : 'secondary.main',
                    borderColor: 'secondary.main',
                    '&:hover': {
                      bgcolor: dateRange === option.value ? 'secondary.dark' : 'action.hover',
                      borderColor: 'secondary.main'
                    }
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </ButtonGroup>
            {dateRange === 'CUSTOM' && customStartDate && customEndDate && (
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                {customStartDate} to {customEndDate}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Custom Date Range Dialog */}
      <Dialog open={customDateOpen} onClose={() => setCustomDateOpen(false)}>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, bgcolor: 'background.paper', color: 'text.primary' }}>Select Date Range</DialogTitle>
        <DialogContent sx={{ bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Start Date"
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'background.paper' }}>
          <Button onClick={() => setCustomDateOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={() => {
              if (customStartDate && customEndDate) {
                setDateRange('CUSTOM');
                setCustomDateOpen(false);
              }
            }}
            variant="contained"
            sx={{
              '&:disabled': { bgcolor: 'action.disabled', color: 'text.disabled' },
              textTransform: 'none'
            }}
            disabled={!customStartDate || !customEndDate}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payments Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : payments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <PaymentIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                No Payments Recorded
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 3 }}>
                Click "Record Payment" to add your first payment
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead sx={getBrandedTableHeadSx(theme, isPlatform ? 'platform' : 'tenant')}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Organization</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Invoice</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Method</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Destination Bank</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Recorded By</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell sx={{ fontSize: '0.75rem' }}>
                        {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-GB') : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                          {payment.organization_name || 'N/A'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                          {payment.organization_code || ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>
                        {payment.invoice_number ? (
                          <Chip
                            icon={<InvoiceIcon sx={{ fontSize: '0.75rem !important' }} />}
                            label={payment.organization_code
                              ? `INV${payment.organization_code}${(payment.invoice_number || '').replace(/^INV-?/, '')}`
                              : payment.invoice_number}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 22 }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
                            No invoice
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#73BF69' }}>
                        {formatCurrency(payment.amount)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>
                        {payment.payment_method ? payment.payment_method.replace(/_/g, ' ') : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>
                        {payment.destination_bank_account_name ? (
                          <>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                              {payment.destination_bank_account_name}
                            </Typography>
                            {payment.destination_bank_name && (
                              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                {payment.destination_bank_name}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {(payment.payment_gateway || payment.paymentGateway) ? (
                          <Chip
                            label={(payment.payment_gateway || payment.paymentGateway) === 'NGENIUS' ? 'Online · NGenius' : 'Offline'}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 22 }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.payment_status || 'PENDING'}
                          size="small"
                          color={
                            payment.payment_status === 'PAID' ? 'success' :
                            payment.payment_status === 'REJECTED' ? 'error' : 'warning'
                          }
                          icon={
                            payment.payment_status === 'PAID' ? <SyncedIcon fontSize="small" /> :
                            payment.payment_status === 'REJECTED' ? <FailedIcon fontSize="small" /> :
                            <PendingIcon fontSize="small" />
                          }
                          sx={{ fontSize: '0.7rem', height: 24 }}
                        />
                        {payment.converted_to_topup && (
                          <Chip
                            label="Topup-only"
                            size="small"
                            sx={{ fontSize: '0.65rem', height: 20, ml: 0.5, bgcolor: 'warning.light', color: 'warning.dark' }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{payment.created_by || 'system'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedPayment(payment);
                              setOpenDetailsDialog(true);
                            }}
                            title="View Details"
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          {payment.thaklis_topup_request_id && payment.payment_status === 'PENDING' && (
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleRefreshApprovalStatus(payment)}
                              title="Check status from Thaklis"
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Record Payment Dialog */}
      <Dialog open={openRecordDialog} onClose={() => setOpenRecordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            Record Organization Payment
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2, '& .MuiInputBase-root': { fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.85rem' }, '& .MuiMenuItem-root': { fontSize: '0.85rem' }, '& .MuiTypography-root': { fontSize: '0.85rem' }, '& .MuiFormHelperText-root': { fontSize: '0.7rem' } }}>
            <Alert severity="info" sx={{ fontSize: '0.75rem', py: 0.5 }}>
              Select an invoice to link this payment. When synced to Thaklis, tickets from the invoice will be marked as paid.
            </Alert>

            {/* Payment Type — Offline (bank/cash + proof) vs Online (hosted card) */}
            <FormControl component="fieldset" size="small">
              <FormLabel component="legend" sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 0.5 }}>
                Payment Type
              </FormLabel>
              <RadioGroup
                value={formData.paymentType}
                onChange={(e) => setFormData({ ...formData, paymentType: e.target.value })}
                sx={{ gap: 0 }}
              >
                <FormControlLabel
                  value="OFFLINE"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">Offline Payment (Bank / Cash with proof)</Typography>}
                  sx={{ mb: -0.5 }}
                />
                <FormControlLabel
                  value="GATEWAY"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">Online Payment (Card)</Typography>}
                />
              </RadioGroup>
            </FormControl>

            {formData.paymentType === 'GATEWAY' && (
              <FormControl size="small" required>
                <InputLabel>Payment Gateway</InputLabel>
                <Select
                  value={formData.paymentGateway}
                  label="Payment Gateway"
                  onChange={(e) => setFormData({ ...formData, paymentGateway: e.target.value })}
                >
                  {PAYMENT_GATEWAYS.map(g => (
                    <MenuItem key={g.id} value={g.id} disabled={!g.enabled}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">
                          {g.label}{!g.enabled && ' — Coming soon'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{g.note}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Autocomplete
              options={getAvailableOrganizations()}
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              value={getAvailableOrganizations().find(o => o.id === formData.organizationId) || null}
              onChange={(e, newValue) => setFormData({
                ...formData,
                organizationId: newValue?.id || null,
                invoiceId: null,  // Reset invoice when org changes
                amount: ''  // Reset amount when org changes
              })}
              renderInput={(params) => (
                <TextField {...params} label="Select Organization" required size="small" />
              )}
              size="small"
            />

            {formData.organizationId && invoicesFetchError && (
              <Alert severity="error" sx={{ fontSize: '0.75rem' }}>
                {invoicesFetchError}
              </Alert>
            )}

            {formData.organizationId && (
              <Autocomplete
                options={availableInvoices}
                loading={loadingInvoices}
                getOptionLabel={(option) => {
                  const optGT = parseFloat(option.grand_total || 0);
                  const optInternal = parseFloat(option.internal_total || 0);
                  const optExternal = parseFloat(option.external_total || 0);
                  // Format invoice number with org code (e.g., INVGOU00037)
                  const formattedInvNum = option.organization_code
                    ? `INV${option.organization_code}${(option.invoice_number || '').replace(/^INV-?/, '')}`
                    : option.invoice_number;
                  if (optExternal > 0) {
                    return `${formattedInvNum} - ${optGT.toFixed(2)} AED (Internal: ${optInternal.toFixed(2)}, External: ${optExternal.toFixed(2)})`;
                  }
                  return `${formattedInvNum} - ${optGT.toFixed(2)} AED (${option.line_items?.length || 0} tickets)`;
                }}
                value={availableInvoices.find(inv => inv.id === formData.invoiceId) || null}
                onChange={(e, newValue) => {
                  // Use adjusted_internal_total if available (excludes already-paid tickets)
                  const adjustedAmount = parseFloat(newValue?.adjustedInternalTotal || newValue?.adjusted_internal_total || 0);
                  const internalAmount = parseFloat(newValue?.internal_total || 0);
                  // Priority: adjusted (excludes paid tickets) > internal > grand total
                  const syncAmount = adjustedAmount > 0
                    ? adjustedAmount
                    : (internalAmount > 0 ? internalAmount : parseFloat(newValue?.grand_total || 0));
                  setFormData({
                    ...formData,
                    invoiceId: newValue?.id || null,
                    amount: newValue ? syncAmount.toFixed(2) : ''
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Invoice (Recommended)"
                    size="small"
                    helperText={
                      invoicesFetchError
                        ? ''
                        : (availableInvoices.length === 0 && !loadingInvoices
                            ? 'No finalized invoices available for this organization'
                            : '')
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingInvoices ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                size="small"
                renderOption={(props, option) => {
                  const optGrandTotal = parseFloat(option.grand_total || 0);
                  const optInternalTotal = parseFloat(option.internal_total || 0);
                  const optExternalTotal = parseFloat(option.external_total || 0);
                  const hasExternal = optExternalTotal > 0;
                  const paidCount = option.paidTicketCount || option.paid_ticket_count || 0;
                  const adjustedTotal = parseFloat(option.adjustedInternalTotal || option.adjusted_internal_total || optInternalTotal);
                  const hasPaidTickets = paidCount > 0;
                  const formattedInvNum = option.organization_code
                    ? `INV${option.organization_code}${(option.invoice_number || '').replace(/^INV-?/, '')}`
                    : option.invoice_number;
                  return (
                    <li {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formattedInvNum}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {optGrandTotal.toFixed(2)} AED
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {option.line_items?.length || 0} ticket(s)
                          </Typography>
                          {hasExternal && (
                            <Typography variant="caption" sx={{ color: 'info.main' }}>
                              Internal: {optInternalTotal.toFixed(2)} | External: {optExternalTotal.toFixed(2)}
                            </Typography>
                          )}
                        </Box>
                        {hasPaidTickets && (
                          <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600, mt: 0.25 }}>
                            {paidCount} ticket(s) already paid on platform — Adjusted: {adjustedTotal.toFixed(2)} AED
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
              />
            )}

            {formData.invoiceId && (() => {
              const selectedInvoice = availableInvoices.find(inv => inv.id === formData.invoiceId);
              const grandTotal = parseFloat(selectedInvoice?.grand_total || 0);
              const internalTotal = parseFloat(selectedInvoice?.internal_total || 0);
              const externalTotal = parseFloat(selectedInvoice?.external_total || 0);
              const hasExternal = externalTotal > 0;
              const paidCount = selectedInvoice?.paidTicketCount || selectedInvoice?.paid_ticket_count || 0;
              const unpaidCount = selectedInvoice?.unpaidTicketCount || selectedInvoice?.unpaid_ticket_count || 0;
              const alreadyPaid = parseFloat(selectedInvoice?.alreadyPaidAmount || selectedInvoice?.already_paid_amount || 0);
              const adjustedTotal = parseFloat(selectedInvoice?.adjustedInternalTotal || selectedInvoice?.adjusted_internal_total || internalTotal);
              const hasPaidTickets = paidCount > 0;
              const ticketDetails = selectedInvoice?.ticketPaymentDetails || selectedInvoice?.ticket_payment_details || [];

              return (
                <>
                  <Alert severity={hasPaidTickets ? "warning" : "success"} sx={{ fontSize: '0.75rem' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {hasPaidTickets ? 'Adjusted Amount — Some tickets already paid' : 'Invoice selected - Amount auto-filled'}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption">Total Invoice Amount:</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {grandTotal.toFixed(2)} AED
                          </Typography>
                        </Box>
                        {hasExternal && (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption">Internal (Thaklis tickets):</Typography>
                              <Typography variant="caption">
                                {internalTotal.toFixed(2)} AED
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" sx={{ color: 'success.main' }}>External (Your services):</Typography>
                              <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                                {externalTotal.toFixed(2)} AED
                              </Typography>
                            </Box>
                          </>
                        )}
                        {hasPaidTickets && (
                          <>
                            <Box sx={{ borderTop: '1px dashed #ccc', mt: 1, pt: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Already paid on platform ({paidCount} ticket{paidCount !== 1 ? 's' : ''}):</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
                                  {alreadyPaid.toFixed(2)} AED
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>Remaining to pay ({unpaidCount} ticket{unpaidCount !== 1 ? 's' : ''}):</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.main' }}>
                                  {adjustedTotal.toFixed(2)} AED
                                </Typography>
                              </Box>
                            </Box>
                          </>
                        )}
                        <Box sx={{ borderTop: '1px dashed #ccc', mt: 1, pt: 1 }}>
                          <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 600 }}>
                            {hasPaidTickets
                              ? `Only ${adjustedTotal.toFixed(2)} AED will be synced to Thaklis (unpaid tickets only)`
                              : hasExternal
                                ? `Only ${internalTotal.toFixed(2)} AED will be synced to Thaklis (internal tickets only)`
                                : 'When synced to Thaklis, tickets from this invoice will be marked as paid.'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Alert>

                  {/* Per-ticket breakdown when some are paid */}
                  {hasPaidTickets && ticketDetails.length > 0 && (
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: '0.75rem' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                        Ticket Details:
                      </Typography>
                      {ticketDetails.map((t, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25 }}>
                          <Typography variant="caption">
                            {t.ticketNumber || t.ticket_number} — {t.serviceName || t.service_name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption">
                              {parseFloat(t.cost || 0).toFixed(2)} AED
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color: (t.paidOnPlatform || t.paid_on_platform) ? 'success.main' : 'warning.main',
                                minWidth: 50,
                                textAlign: 'right'
                              }}
                            >
                              {(t.paidOnPlatform || t.paid_on_platform) ? 'PAID' : 'UNPAID'}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              );
            })()}

            <TextField
              label="Amount (AED)"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
              size="small"
              disabled={!!formData.invoiceId}  // Disable if invoice selected (auto-filled)
              InputProps={{
                startAdornment: <InputAdornment position="start">AED</InputAdornment>
              }}
              helperText={formData.invoiceId ? "Base amount (ticket cost to Thaklis) - auto-filled from invoice" : ""}
            />

            {formData.paymentType === 'OFFLINE' && (
              <FormControl size="small" required>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                  label="Payment Method"
                >
                  <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
                  <MenuItem value="CASH">Cash</MenuItem>
                  <MenuItem value="CARD">Card</MenuItem>
                  <MenuItem value="CHEQUE">Cheque</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              label="Payment Date"
              type="date"
              value={formData.paymentDate}
              onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
              required
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            {formData.paymentType === 'OFFLINE' && (
              <>
                <TextField
                  label="Reference Number (Optional)"
                  value={formData.referenceNumber}
                  onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
                  size="small"
                />

                <TextField
                  label="Bank Deposit Date"
                  type="date"
                  value={formData.bankDepositDate}
                  onChange={(e) => setFormData({ ...formData, bankDepositDate: e.target.value })}
                  required
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}

            <TextField
              label="Notes (Optional)"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
              size="small"
            />

            {formData.paymentType === 'GATEWAY' && computePlatformFee(formData.amount) > 0 && (
              <Box sx={{ p: 1.5, mb: 1, border: '1px solid #ffe0a3', bgcolor: '#fff8e1', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption">Amount:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>{parseFloat(formData.amount || 0).toFixed(2)} AED</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption">Platform Fee:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>+ {computePlatformFee(formData.amount).toFixed(2)} AED</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Total to Pay:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.main' }}>
                    {(parseFloat(formData.amount || 0) + computePlatformFee(formData.amount)).toFixed(2)} AED
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                  The platform fee is charged by the gateway on top of your payment. Your Thaklis balance is credited the amount only.
                </Typography>
              </Box>
            )}

            {formData.paymentType === 'GATEWAY' && (
              <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                You will be redirected to the {PAYMENT_GATEWAYS.find(g => g.id === formData.paymentGateway)?.label || 'payment'} hosted
                page. After the payment completes you will be returned to this portal, and the status will be reflected within 30 minutes.
              </Alert>
            )}

            {formData.paymentType === 'OFFLINE' && (
              <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                Offline payments are reviewed manually. The status will be reflected within 24 hours after submission.
              </Alert>
            )}

            {/* Destination Bank Account Selection - Only Operation Accounts (offline only) */}
            {formData.paymentType === 'OFFLINE' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BankIcon fontSize="small" color="primary" />
                Destination Bank Account (Operation)
              </Typography>
              <Autocomplete
                options={bankAccounts.filter(b =>
                  b.type === 'OPERATIONS' && b.status === 'ACTIVE'
                )}
                getOptionLabel={(option) => `${option.accountName} - ${option.bankName}`}
                value={bankAccounts.find(b => b.id === formData.destinationBankAccountId) || null}
                onChange={(e, newValue) => setFormData({
                  ...formData,
                  destinationBankAccountId: newValue?.id || null
                })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Destination Bank Account"
                    required
                    size="small"
                    helperText={bankAccounts.length === 0 ? "Loading bank accounts..." : "Select the Thaklis operation bank account where payment was deposited"}
                  />
                )}
                size="small"
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.accountName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {option.bankName} | {option.accountNumber || option.iban}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
              {formData.destinationBankAccountId && (
                <Alert severity="info" sx={{ mt: 1, fontSize: '0.7rem' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Payment will be sent to:
                    </Typography>
                    {(() => {
                      const selectedBank = bankAccounts.find(b => b.id === formData.destinationBankAccountId);
                      return selectedBank ? (
                        <>
                          <Typography variant="caption">{selectedBank.bankName}</Typography>
                          <Typography variant="caption">Account: {selectedBank.accountNumber}</Typography>
                          <Typography variant="caption">IBAN: {selectedBank.iban}</Typography>
                        </>
                      ) : null;
                    })()}
                  </Box>
                </Alert>
              )}
            </Box>
            )}

            {/* Source Bank Account — TC's own account auto-debited on confirmation (offline only) */}
            {formData.paymentType === 'OFFLINE' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BankIcon fontSize="small" color="primary" />
                Pay From (Your Bank Account)
              </Typography>
              <Autocomplete
                options={sourceBankAccounts}
                getOptionLabel={(option) => `${option.bank_name} (${option.account_type})`}
                value={sourceBankAccounts.find(b => b.id === formData.sourceBankAccountId) || null}
                onChange={(e, newValue) => setFormData({
                  ...formData,
                  sourceBankAccountId: newValue?.id || null
                })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Source Bank Account"
                    required
                    size="small"
                    helperText={sourceBankAccounts.length === 0
                      ? "No active bank accounts — add one under Wallets"
                      : "This account is auto-debited when the payment is confirmed"}
                  />
                )}
                size="small"
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.bank_name} ({option.account_type})
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {option.account_number || option.iban} · Balance {formatCurrency(option.balance)}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
              {formData.sourceBankAccountId && (
                sourceBankInfoLoading ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Checking available balance…
                  </Typography>
                ) : sourceBankInfo && (
                  <Alert
                    severity={amountExceedsAvailable ? 'error' : 'info'}
                    sx={{ mt: 1, fontSize: '0.7rem' }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Typography variant="caption">Balance: {formatCurrency(sourceBankInfo.balance)}</Typography>
                      {Number(sourceBankInfo.pendingDebits) > 0 && (
                        <Typography variant="caption">
                          Reserved by pending payments: {formatCurrency(sourceBankInfo.pendingDebits)}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Available to pay: {formatCurrency(sourceBankInfo.available)}
                      </Typography>
                      {amountExceedsAvailable && (
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Amount exceeds available balance — reduce the amount or top up this account.
                        </Typography>
                      )}
                    </Box>
                  </Alert>
                )
              )}
            </Box>
            )}

            {/* Receipt Upload Section (offline only) */}
            {formData.paymentType === 'OFFLINE' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <UploadIcon fontSize="small" color="primary" />
                Upload Payment Proof
              </Typography>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: formData.receiptFile ? 'success.main' : 'divider',
                  borderRadius: 1,
                  p: 2,
                  textAlign: 'center',
                  bgcolor: formData.receiptFile ? (isDark ? 'rgba(46,125,50,0.15)' : '#e8f5e9') : 'action.hover',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: isDark ? 'rgba(87,148,242,0.12)' : '#e3f2fd'
                  }
                }}
                onClick={() => document.getElementById('receipt-upload-input').click()}
              >
                <input
                  id="receipt-upload-input"
                  type="file"
                  hidden
                  accept="image/*,.pdf"
                  onChange={handleReceiptUpload}
                />
                {uploadingReceipt ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Uploading...</Typography>
                  </Box>
                ) : formData.receiptFile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <FileIcon color="success" />
                    <Typography variant="body2" color="success.main">
                      {formData.receiptFilename || formData.receiptFile.name}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData(prev => ({
                          ...prev,
                          receiptFile: null,
                          receiptUrl: null,
                          receiptS3Key: null,
                          receiptFilename: null
                        }));
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box>
                    <UploadIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Click to select image or PDF
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Maximum file size: 10MB
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Upload bank transfer receipt, deposit slip, or payment confirmation
              </Typography>
            </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setOpenRecordDialog(false); resetForm(); }} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.organizationId ||
              !formData.invoiceId ||
              (formData.paymentType === 'OFFLINE' && (
                !formData.amount ||
                parseFloat(formData.amount) <= 0 ||
                !formData.destinationBankAccountId ||
                !formData.sourceBankAccountId ||
                amountExceedsAvailable
              )) ||
              (formData.paymentType === 'GATEWAY' && (
                !formData.paymentGateway ||
                !PAYMENT_GATEWAYS.find(g => g.id === formData.paymentGateway)?.enabled
              )) ||
              uploadingReceipt ||
              initiatingOnline
            }
            sx={{ textTransform: 'none' }}
          >
            {initiatingOnline ? 'Redirecting…' : (uploadingReceipt ? 'Uploading...' : (formData.paymentType === 'GATEWAY' ? 'Pay Online' : 'Record Payment'))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Payment Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    Organization
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    {selectedPayment.organization_name}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    {selectedPayment.organization_code}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    Amount
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#73BF69' }}>
                    {formatCurrency(selectedPayment.amount)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    Payment Method
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {selectedPayment.payment_method ? selectedPayment.payment_method.replace(/_/g, ' ') : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    Payment Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {selectedPayment.payment_date ? new Date(selectedPayment.payment_date).toLocaleDateString('en-GB') : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    Reference Number
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {selectedPayment.payment_reference || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    Bank Deposit Reference
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {selectedPayment.bank_deposit_reference || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    Notes
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', fontFamily: 'monospace', bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                    {selectedPayment.notes || 'No notes'}
                  </Typography>
                </Grid>
                {selectedPayment.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      Notes
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {selectedPayment.notes}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    Recorded By
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {selectedPayment.recordedByUserName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    Recorded At
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {formatDate(selectedPayment.created_at || selectedPayment.createdAt)}
                  </Typography>
                </Grid>

                {/* Destination Bank Account */}
                {selectedPayment.destination_bank_account_name && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: isDark ? 'rgba(87,148,242,0.12)' : '#e3f2fd', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BankIcon fontSize="small" />
                        Destination Bank Account
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem', mt: 0.5 }}>
                        {selectedPayment.destination_bank_account_name}
                      </Typography>
                      {selectedPayment.destination_bank_name && (
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                          {selectedPayment.destination_bank_name}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}

                {/* Payment Receipt */}
                {selectedPayment.receipt_url && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: isDark ? 'rgba(46,125,50,0.12)' : '#e8f5e9', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FileIcon fontSize="small" />
                        Payment Receipt
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                          {selectedPayment.receipt_filename || 'Receipt uploaded'}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => window.open(selectedPayment.receipt_url, '_blank')}
                          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                        >
                          View Receipt
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDetailsDialog(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Topup Wallet Confirmation Dialog */}
      <Dialog open={openTopupDialog} onClose={() => setOpenTopupDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Topup Organization Wallet
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
            This will create a topup request in Thaklis to credit the organization's wallet.
          </Alert>
          {selectedPayment && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', mb: 1 }}>
                    <strong>Payment From:</strong> {selectedPayment.organization_name || 'N/A'} ({selectedPayment.organization_code || ''})
                  </Typography>
                </Grid>
                {bankAccountsError && (
                  <Grid item xs={12}>
                    <Alert severity="warning" sx={{ fontSize: '0.75rem' }}>
                      <strong>Warning:</strong> {bankAccountsError}.
                    </Alert>
                  </Grid>
                )}
                {!bankAccountsError && bankAccounts.length > 0 && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                      <strong>Destination Bank Account:</strong> {bankAccounts[0].accountName || `Bank Account ID: ${bankAccounts[0].id}`}
                      {bankAccounts[0].accountNumber && (
                        <> ({bankAccounts[0].accountNumber})</>
                      )}
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Autocomplete
                    options={getAvailableOrganizations()}
                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                    value={getAvailableOrganizations().find(o => o.id === topupDestinationOrgId) || null}
                    onChange={(e, newValue) => setTopupDestinationOrgId(newValue?.id || null)}
                    renderInput={(params) => (
                      <TextField {...params} label="Credit Organization Wallet" required size="small" />
                    )}
                    size="small"
                  />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5, display: 'block' }}>
                    Pre-selected to payment source. Change only if crediting a different organization (e.g., payment on behalf of another).
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', mb: 1 }}>
                    <strong>Payment Total:</strong> {formatCurrency(selectedPayment.amount)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', mb: 1 }}>
                    <strong>Already Topped Up:</strong> {formatCurrency(selectedPayment.payment_status === 'PAID' ? selectedPayment.amount : 0)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', mb: 1, color: '#5794F2', fontWeight: 600 }}>
                    <strong>Remaining Balance:</strong> {formatCurrency(selectedPayment.payment_status === 'PAID' ? 0 : parseFloat(selectedPayment.amount))}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Topup Amount (AED)"
                    type="number"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                    required
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">AED</InputAdornment>
                    }}
                    helperText={`Enter amount between 0.01 and ${formatCurrency(selectedPayment.payment_status === 'PAID' ? 0 : parseFloat(selectedPayment.amount))}`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', mb: 1 }}>
                    <strong>Bank Deposit Reference:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', fontFamily: 'monospace', bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                    {selectedPayment.bank_deposit_reference || selectedPayment.payment_reference || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
              {topupDestinationOrgId !== selectedPayment.organization_id && (
                <Alert severity="warning" sx={{ mt: 2, fontSize: '0.75rem' }}>
                  <strong>Warning:</strong> You are topping up a DIFFERENT organization than the payment source. Payment from <strong>{selectedPayment.organization_name}</strong> will credit <strong>{getAvailableOrganizations().find(o => o.id === topupDestinationOrgId)?.name}</strong>'s wallet.
                </Alert>
              )}
              <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem' }}>
                <strong>Note:</strong> In production, this will create a topup request in Thaklis that requires admin approval. The status will be PARTIAL if amount is less than the remaining balance, or SYNCED if the full remaining balance is topped up.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setOpenTopupDialog(false); setTopupAmount(''); setTopupDestinationOrgId(null); }} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleTopupWallet}
            variant="contained"
            color="primary"
            startIcon={<TopupIcon />}
            disabled={!topupAmount || !topupDestinationOrgId || parseFloat(topupAmount) <= 0 || (selectedPayment?.payment_status === 'PAID') || parseFloat(topupAmount) > parseFloat(selectedPayment?.amount || 0)}
            sx={{ textTransform: 'none' }}
          >
            Topup {topupAmount && parseFloat(topupAmount) > 0 ? formatCurrency(parseFloat(topupAmount)) : 'Wallet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Direct Topup Dialog (No Payment Record) */}
      <Dialog open={openDirectTopupDialog} onClose={() => setOpenDirectTopupDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Direct Wallet Topup
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
              <strong>Direct Topup:</strong> Credit an organization's wallet directly without recording a payment. Use for promotional credits, refunds, or advances.
            </Alert>

            <Autocomplete
              options={getAvailableOrganizations()}
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              value={getAvailableOrganizations().find(o => o.id === directTopupData.organizationId) || null}
              onChange={(e, newValue) => setDirectTopupData({ ...directTopupData, organizationId: newValue?.id || null })}
              renderInput={(params) => (
                <TextField {...params} label="Select Organization" required size="small" />
              )}
              size="small"
            />

            <TextField
              label="Topup Amount (AED)"
              type="number"
              value={directTopupData.amount}
              onChange={(e) => setDirectTopupData({ ...directTopupData, amount: e.target.value })}
              required
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start">AED</InputAdornment>
              }}
            />

            <FormControl size="small" required>
              <InputLabel>Reason for Topup</InputLabel>
              <Select
                value={directTopupData.reason}
                onChange={(e) => setDirectTopupData({ ...directTopupData, reason: e.target.value })}
                label="Reason for Topup"
              >
                <MenuItem value="PROMOTIONAL_CREDIT">Promotional Credit</MenuItem>
                <MenuItem value="GOODWILL_CREDIT">Goodwill / Compensation</MenuItem>
                <MenuItem value="ADVANCE_PAYMENT">Advance Payment</MenuItem>
                <MenuItem value="REFUND">Refund</MenuItem>
                <MenuItem value="CORRECTION">Accounting Correction</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Notes (Optional)"
              value={directTopupData.notes}
              onChange={(e) => setDirectTopupData({ ...directTopupData, notes: e.target.value })}
              multiline
              rows={2}
              size="small"
              placeholder="Add any additional details..."
            />

            <Alert severity="warning" sx={{ fontSize: '0.75rem' }}>
              <strong>Note:</strong> This will immediately create a SYNCED topup request in Thaklis (subject to admin approval in production). No payment record will be created.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => {
            setOpenDirectTopupDialog(false);
            setDirectTopupData({ organizationId: null, amount: '', reason: '', notes: '' });
          }} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDirectTopup}
            variant="contained"
            color="primary"
            startIcon={<TopupIcon />}
            disabled={!directTopupData.organizationId || !directTopupData.amount || !directTopupData.reason || parseFloat(directTopupData.amount) <= 0}
            sx={{ textTransform: 'none' }}
          >
            Topup {directTopupData.amount && parseFloat(directTopupData.amount) > 0 ? formatCurrency(parseFloat(directTopupData.amount)) : 'Wallet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sync to Thaklis Dialog */}
      <SyncToThaklisDialog
        open={openSyncDialog}
        onClose={() => {
          setOpenSyncDialog(false);
          setSelectedPayment(null);
        }}
        payment={selectedPayment}
        onSyncComplete={handleSyncComplete}
      />
    </Box>
  );
};

export default Payments;
